import { useEffect, useMemo, useState } from 'react'
import { useGroupCatering } from '../../hooks/useGroupCatering.js'
import { useGroupCateringSuggest } from '../../hooks/useGroupCateringSuggest.js'
import { formatMoney } from '../../lib/format.js'
import { AGENT_LABELS } from '../../lib/agents.js'
import CateringInquiryRow from '../CateringInquiryRow.jsx'
import CateringInquiryModal from '../CateringInquiryModal.jsx'
import { Skeleton, SkeletonCard, SkeletonList } from '../Skeleton.jsx'
import Toast from '../Toast.jsx'

// GroupCateringPipeline — the combined, group-level catering view (Ship 4).
// Rendered from GroupDashboardScreen when view==='catering-breakdown'.
//
// Shows EVERY unit's catering inquiries in one pipeline with the same
// four-tab status workflow as the single-unit CateringScreen, plus a
// unit-filter chip row and a color-coded assigned-unit chip on each row.
// Creating a new inquiry opens CateringInquiryModal in group-create mode
// (units + AI unit-suggestion). NO AI-cost / model text anywhere.
//
// House rule (React #310): ALL hooks fire BEFORE any conditional return.

const TABS = [
  { id: 'new',       label: 'New' },
  { id: 'quoted',    label: 'Quoted' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'history',   label: 'History' },
]

// Stable color ramp for unit chips, keyed by the unit's index in the
// (name-sorted) units list. Each entry is a {bg,text,dot} triple in the
// app's calm palette. Index past the end wraps with modulo.
const UNIT_COLORS = [
  { bg: '#EFF6FF', text: '#1D4ED8', dot: '#3B82F6' }, // blue
  { bg: '#FEF3F2', text: '#B42318', dot: '#F04438' }, // red
  { bg: '#ECFDF3', text: '#027A48', dot: '#12B76A' }, // green
  { bg: '#FFFAEB', text: '#B54708', dot: '#F79009' }, // amber
  { bg: '#F4F3FF', text: '#5925DC', dot: '#7A5AF8' }, // violet
  { bg: '#FDF2FA', text: '#C11574', dot: '#EE46BC' }, // pink
  { bg: '#F0F9FF', text: '#026AA2', dot: '#0BA5EC' }, // cyan
]

export default function GroupCateringPipeline({ groupId, onBack }) {
  // --- All hooks BEFORE any conditional return (React #310). ---
  const {
    byStatus,
    units,
    unitsById,
    loading,
    inquiries,
    createInquiry,
    updateInquiry,
  } = useGroupCatering(groupId)

  // Suggest hook owns the edge-fn call + its own in-flight state. We pass
  // its suggest() to the modal as onSuggestUnit.
  const { suggest } = useGroupCateringSuggest(groupId)

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [toast, setToast] = useState('')
  const [activeTab, setActiveTab] = useState(null) // null until first compute
  const [unitFilter, setUnitFilter] = useState('all') // 'all' | <unit id>

  // Stable color per unit, by index in the name-sorted units list.
  const unitColorById = useMemo(() => {
    const m = new Map()
    units.forEach((u, idx) => m.set(u.id, UNIT_COLORS[idx % UNIT_COLORS.length]))
    return m
  }, [units])

  // Apply the unit filter to a status bucket. 'all' → untouched.
  const filterByUnit = useMemo(() => {
    if (unitFilter === 'all') return (list) => list
    return (list) => list.filter((i) => i.assigned_restaurant_id === unitFilter)
  }, [unitFilter])

  // History group — completed + cancelled + declined, most recent first.
  // Same grouping CateringScreen uses, then unit-filtered.
  const historyList = useMemo(() => {
    const all = [...byStatus.completed, ...byStatus.cancelled, ...byStatus.declined]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    return filterByUnit(all)
  }, [byStatus.completed, byStatus.cancelled, byStatus.declined, filterByUnit])

  const tabCounts = useMemo(() => ({
    new:       filterByUnit(byStatus.new).length,
    quoted:    filterByUnit(byStatus.quoted).length,
    confirmed: filterByUnit(byStatus.confirmed).length,
    history:   historyList.length,
  }), [byStatus.new, byStatus.quoted, byStatus.confirmed, historyList, filterByUnit])

  const defaultTab = useMemo(() => {
    if (tabCounts.new > 0)       return 'new'
    if (tabCounts.quoted > 0)    return 'quoted'
    if (tabCounts.confirmed > 0) return 'confirmed'
    return 'history'
  }, [tabCounts.new, tabCounts.quoted, tabCounts.confirmed])

  useEffect(() => {
    if (activeTab == null) setActiveTab(defaultTab)
  }, [defaultTab, activeTab])

  const visibleInquiries = useMemo(() => {
    if (activeTab === 'history') return historyList
    if (activeTab === 'quoted' || activeTab === 'confirmed') {
      const list = filterByUnit(activeTab === 'quoted' ? byStatus.quoted : byStatus.confirmed)
      return [...list].sort((a, b) => {
        const da = a.event_date ? new Date(a.event_date) : new Date('9999-12-31')
        const db = b.event_date ? new Date(b.event_date) : new Date('9999-12-31')
        return da - db
      })
    }
    return [...filterByUnit(byStatus.new)].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  }, [activeTab, byStatus.new, byStatus.quoted, byStatus.confirmed, historyList, filterByUnit])

  // Group-wide pipeline summary (across all units, not unit-filtered —
  // these are the headline group numbers).
  const newCount = byStatus.new.length
  const activeCount = byStatus.quoted.length + byStatus.confirmed.length
  const pipelineValue = [...byStatus.quoted, ...byStatus.confirmed]
    .reduce((s, i) => s + Number(i.estimated_total || 0), 0)

  async function handleCreate(payload) {
    await createInquiry(payload)
    setToast(`Added inquiry from ${payload.contact_name?.trim() || 'customer'}.`)
  }

  async function handleSave(id, patch) {
    const updated = await updateInquiry(id, patch)
    // Resync the open editing row so the modal reflects the post-update
    // state (status, *_at stamps) without a close+reopen — same pattern
    // as CateringScreen.handleSave (M1).
    if (updated) setEditing(updated)
    setToast('Inquiry saved.')
    return updated
  }

  // Build the assigned-unit chip descriptor for a row.
  function assignedUnitFor(inquiry) {
    const uid = inquiry.assigned_restaurant_id
    if (!uid) return null
    const u = unitsById.get(uid)
    if (!u) return null
    return { name: u.name, color: unitColorById.get(uid) }
  }

  // Loading skeleton. HOOK ORDERING: all hooks are above this early return.
  if (loading && inquiries.length === 0) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-6">
        <SkeletonCard />
        <section className="mt-6">
          <Skeleton className="h-4 w-32" />
          <SkeletonList rows={4} className="mt-2" />
        </section>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-screen-sm px-4 py-6">
      {/* Back affordance → group dashboard. */}
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-black/60 hover:text-black/85"
      >
        ← All units
      </button>

      {/* ---------- Group pipeline summary ----------------------- */}
      <section
        className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
        aria-label={AGENT_LABELS.groupCateringPipeline}
      >
        <div className="text-xs font-medium text-black/55">
          {AGENT_LABELS.groupCateringPipeline}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
          <div className="rounded-lg bg-blue-50 px-3 py-2">
            <div className="text-xs text-blue-900/70">New</div>
            <div className="text-2xl font-medium tabular-nums text-blue-700">{newCount}</div>
          </div>
          <div className="rounded-lg bg-amber-50 px-3 py-2">
            <div className="text-xs text-amber-900/70">Active</div>
            <div className="text-2xl font-medium tabular-nums text-amber-700">{activeCount}</div>
            <div className="text-xs text-amber-900/60 mt-0.5">quoted + confirmed</div>
          </div>
          <div className="rounded-lg bg-emerald-50 px-3 py-2">
            <div className="text-xs text-emerald-900/70">Pipeline $</div>
            <div className="text-2xl font-medium tabular-nums text-emerald-700">
              {formatMoney(pipelineValue)}
            </div>
            <div className="text-xs text-emerald-900/60 mt-0.5">estimated total</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={!units || units.length === 0}
          title={!units || units.length === 0 ? 'Add an active unit to this group first' : undefined}
          className="mt-4 min-h-btn w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + New inquiry
        </button>
      </section>

      {/* ---------- Unit filter chips ---------------------------- */}
      <nav aria-label="Filter by location" className="mt-4 overflow-x-auto">
        <div className="inline-flex min-w-full items-center gap-2 pb-1">
          <UnitChip
            label="All units"
            active={unitFilter === 'all'}
            onClick={() => setUnitFilter('all')}
          />
          {units.map((u) => (
            <UnitChip
              key={u.id}
              label={u.name}
              color={unitColorById.get(u.id)}
              active={unitFilter === u.id}
              onClick={() => setUnitFilter(u.id)}
            />
          ))}
        </div>
      </nav>

      {/* ---------- Status segmented control --------------------- */}
      <nav aria-label="Catering status filter" className="mt-3 overflow-x-auto">
        <div className="inline-flex min-w-full items-center gap-1 rounded-full bg-black/5 p-1">
          {TABS.map((t) => {
            const isActive = activeTab === t.id
            const count = tabCounts[t.id] ?? 0
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                aria-pressed={isActive}
                className={[
                  'whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'bg-amber-brand-200 font-semibold text-amber-brand-800'
                    : 'text-black/60 hover:text-black/80',
                ].join(' ')}
              >
                {t.label} <span className="ml-1 text-xs tabular-nums opacity-75">({count})</span>
              </button>
            )
          })}
        </div>
      </nav>

      {/* ---------- Tab body ------------------------------------- */}
      <section className="mt-4" aria-label={`${activeTab || ''} inquiries`}>
        {visibleInquiries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-black/15 px-4 py-10 text-center text-sm text-black/60">
            {tabEmptyMessage(activeTab)}
          </div>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-black/10">
            {visibleInquiries.map((i) => (
              <CateringInquiryRow
                key={i.id}
                inquiry={i}
                onTap={() => setEditing(i)}
                assignedUnit={assignedUnitFor(i)}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Empty state — no inquiries anywhere across the group. */}
      {inquiries.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-black/15 px-4 py-10 text-center text-sm text-black/60">
          <div className="text-base font-medium mb-1">No catering inquiries yet</div>
          <p>
            When customers ask about catering at any of your locations,
            tap <span className="font-medium text-blue-600">+ New inquiry</span> above to log it.
          </p>
        </div>
      )}

      {/* Group-create modal — units + AI unit-suggestion. */}
      <CateringInquiryModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        inquiry={null}
        units={units}
        onSuggestUnit={suggest}
      />

      {/* Edit modal — NO assignment UI (no units / onSuggestUnit passed),
          so it is byte-identical to the single-unit edit surface. No
          onDelete (group-owner policy set is SELECT + UPDATE only). */}
      <CateringInquiryModal
        open={!!editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
        inquiry={editing}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}

function UnitChip({ label, color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors',
        active
          ? 'border-black/20 bg-black/5 font-semibold text-black/85'
          : 'border-black/10 bg-white text-black/60 hover:text-black/80',
      ].join(' ')}
    >
      {color && (
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: color.dot }}
          aria-hidden="true"
        />
      )}
      <span className="max-w-[12rem] truncate">{label}</span>
    </button>
  )
}

function tabEmptyMessage(tab) {
  switch (tab) {
    case 'new':       return 'No new inquiries waiting on you.'
    case 'quoted':    return 'No quotes out for response right now.'
    case 'confirmed': return 'No confirmed events yet.'
    case 'history':   return 'No past events in your history yet.'
    default:          return 'Nothing here yet.'
  }
}
